import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Eye, CalendarRange } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { InlineLoader } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { dateLabel } from "@/lib/status";
import { downloadReport } from "@/lib/download";

export default function History() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    const qp = [];
    if (start) qp.push(`start=${start}`);
    if (end) qp.push(`end=${end}`);
    api.get(`/history${qp.length ? `?${qp.join("&")}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  let body;
  if (loading) {
    body = <InlineLoader />;
  } else if (rows.length === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-stone-300 bg-white py-16 text-stone-400" data-testid="history-empty">
        <CalendarRange className="h-9 w-9" strokeWidth={1.8} />
        <p className="text-sm font-medium">No history found for this range.</p>
      </div>
    );
  } else {
    body = (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => (
          <div key={r.date} className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={`history-card-${r.date}`}>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">{r.date}</p>
              <p className="font-heading text-lg font-bold">{dateLabel(r.date)}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Taken" value={r.total_taken} color="#0284C7" />
              <Stat label="Sold Multiple" value={r.total_sold_multiple} color="#EA580C" />
              <Stat label="Sale Actions" value={r.total_actions} color="#1C1917" />
              <Stat label="Active Workers" value={r.active_workers} color="#0EA5E9" />
            </div>
            <div className="mt-auto flex gap-2">
              <Button onClick={() => navigate(`/admin/report?date=${r.date}`)} data-testid={`history-view-${r.date}`} className="flex-1 gap-1.5 rounded-xl font-semibold">
                <Eye className="h-4 w-4" /> View
              </Button>
              <Button variant="outline" onClick={() => downloadReport(r.date)} data-testid={`history-export-${r.date}`} className="gap-1.5 rounded-xl font-semibold">
                <Download className="h-4 w-4" /> Excel
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5 bb-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">History</h1>
        <p className="text-sm text-stone-500">Previous days — read-only archives</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-stone-200 bg-white p-4">
        <div className="space-y-1">
          <Label className="text-xs font-semibold text-stone-500">From</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="history-start" className="h-10 w-[160px] rounded-xl" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold text-stone-500">To</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="history-end" className="h-10 w-[160px] rounded-xl" />
        </div>
        {(start || end) && (
          <Button variant="ghost" onClick={() => { setStart(""); setEnd(""); }} data-testid="history-clear" className="rounded-xl font-semibold">
            Clear
          </Button>
        )}
      </div>

      {body}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p className="font-heading text-2xl font-black tracking-tight" style={{ color }}>{value}</p>
      <p className="text-xs font-medium text-stone-500">{label}</p>
    </div>
  );
}
